import { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material';
import { StyledTableCellWithoutLeftRight } from 'routes/Main/style';
import Form from 'components/Form';

import { IFormValues, IFormWatch, IFormRegister } from 'routes/Main/type';

import SectionTitle from '../components/SectionTitle';
import { IPatientInfo } from 'apis/admin/type';

const contentLabel = [
  {
    id: 1,
    desc: [
      '환자등록번호: 병원에서 관리하는 환자등록번호 기재',
      '가입자 구분 (코드번호로 기재)',
      '입퇴원여부 (코드번호로 기재)',
    ],
    scores: [0, 5, 6],
    ids: [1, 2, 3],
  },
  {
    id: 2,
    desc: ['생년월일: YYYYMMDD 형식으로 기재'],
    scores: [2],
    ids: [4],
  },
  {
    id: 3,
    desc: ['성별(코드번호로 기재)'],
    scores: [2],
    ids: [5],
  },
  {
    id: 4,
    desc: [
      "상병코드: 주진단명에 해당하는 '한국표준질병사인분류'의 분류기호를 1개만 기재(예: C16.10)",
      '당일수술여부(코드번호로 기재): 1: 수술 실시(수술 당일만), 2: 수술 미실시',
    ],
    scores: [2, 2],
    ids: [6, 7],
  },
];

interface Props extends IFormValues, IFormWatch, IFormRegister {
  disabled?: boolean;
  patientInfo: IPatientInfo;
}

const Contents1 = (props: Props) => {
  const { palette } = useTheme();
  const {
    disabled,
    setValue,
    getValues,
    register,
    patientInfo: { patient_id, patient_virtual_id },
  } = props;

  const min = 1;
  const max = 6;

  // 가입자구분
  const [input1, setInput1] = useState(
    getValues('no02_02') ? Number(getValues('no02_02')) : 1
  );

  // 입퇴원여부
  const [input2, setInput2] = useState(
    getValues('no02_03') ? Number(getValues('no02_03')) : 1
  );

  // 성별
  const [input4, setInput4] = useState(
    getValues('no02_04') ? Number(getValues('no02_04')) : 1
  );

  // 당일수술 여부
  const [input5, setInput5] = useState(
    getValues('no02_05') ? Number(getValues('no02_05')) : 1
  );
  const [input6, setInput6] = useState(
    getValues('no02_06') ? Number(getValues('no02_06')) : 1
  );

  return (
    <>
      <SectionTitle title="2. 환자정보" mb={3} mt={3} />
      <Box sx={{ width: '98%', margin: '20px auto' }}>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <StyledTableCellWithoutLeftRight
                align="left"
                sx={{
                  padding: '16px',
                  color: `${palette.primary.main}`,
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                항목
              </StyledTableCellWithoutLeftRight>
              <StyledTableCellWithoutLeftRight
                align="center"
                sx={{
                  padding: '16px',
                  color: `${palette.primary.main}`,
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                입력
              </StyledTableCellWithoutLeftRight>
            </TableRow>
          </TableHead>
          <TableBody>
            {contentLabel.map(
              (content: {
                id: number;
                desc: string[];
                scores: number[];
                ids: number[];
              }) => (
                <TableRow>
                  <StyledTableCellWithoutLeftRight>
                    {content.desc.map((v, i) => (
                      <TableRow
                        sx={{
                          height: '48px',
                          lineHeight: '48px',
                          borderBottom:
                            i !== content.desc.length - 1
                              ? '1px solid lightgray'
                              : '',
                          width: '100%',
                        }}
                      >
                        <Box
                          sx={{
                            paddingLeft: '16px',
                            fontSize: '12px',
                            width: '82.6vw',
                            maxWidth: '1340px',
                            minWidth: '1088px',
                            fontWeight: 500,
                          }}
                        >
                          {v}
                          <br />
                          {v === '가입자 구분 (코드번호로 기재)' && (
                            <Typography
                              sx={{
                                fontSize: '12px',
                                fontWeight: 500,
                                marginBottom: '10px',
                              }}
                            >
                              1. 건강보험 <br />
                              2. 의료급여 <br />
                              3. 산재보험 <br />
                              4. 자동차보험 <br />
                              5. 기타
                            </Typography>
                          )}
                          {v === '성별(코드번호로 기재)' && (
                            <Typography
                              sx={{
                                fontSize: '12px',
                                fontWeight: 500,
                                marginBottom: '10px',
                                marginTop: '-10px',
                              }}
                            >
                              1. 남성 <br />
                              2. 여성
                            </Typography>
                          )}

                          {v === '입퇴원여부 (코드번호로 기재)' && (
                            <Typography
                              sx={{
                                fontSize: '12px',
                                fontWeight: 500,
                                marginBottom: '10px',
                              }}
                            >
                              1. 재원 <br />
                              2. 입원(전입) <br />
                              3. 퇴원 <br />
                              4. 전출 <br />
                              5. 당일입퇴원 <br />
                              6. 외박(0시~24시 부재 시)
                            </Typography>
                          )}
                          {v ===
                            "상병코드: 주진단명에 해당하는 '한국표준질병사인분류'의 분류기호를 1개만 기재(예: C16.10)" && (
                            <Typography
                              sx={{
                                fontSize: '12px',
                                fontWeight: 500,
                                marginBottom: '10px',
                                marginTop: '-10px',
                                color: 'red',
                              }}
                            >
                              ※ 삼단분류까지 필수 기재(오단분류까지 기재 가능)
                            </Typography>
                          )}
                          {v ===
                            '당일수술여부(코드번호로 기재): 1: 수술 실시(수술 당일만), 2: 수술 미실시' && (
                            <Typography
                              sx={{
                                fontSize: '12px',
                                fontWeight: 500,
                                marginBottom: '10px',
                                marginTop: '-10px',
                                color: 'red',
                              }}
                            >
                              ※ 수술은 '건강보험 행위 급여·비급여 목록표 및 급여
                              상대가치 점수' 제9장, 제10장, 제18장의 항목 중
                              수술에 해당하는 분류항목
                            </Typography>
                          )}
                        </Box>
                      </TableRow>
                    ))}
                  </StyledTableCellWithoutLeftRight>
                  <StyledTableCellWithoutLeftRight>
                    {content.scores.map((score, i) => {
                      if (
                        content.desc[i] ===
                        '환자등록번호: 병원에서 관리하는 환자등록번호 기재'
                      ) {
                        return (
                          <TableRow
                            sx={{
                              height: '48px',
                              lineHeight: '48px',
                              textAlign: 'center',
                              borderBottom:
                                i !== content.desc.length - 1
                                  ? '1px solid lightgray'
                                  : '',
                            }}
                          >
                            <Box
                              sx={{
                                width: '130px',
                                height: '48px',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <Form.MuiTextField
                                value={patient_virtual_id || patient_id}
                                InputProps={{ readOnly: true }}
                              />
                            </Box>
                          </TableRow>
                        );
                      }
                      if (
                        content.desc[i] === '생년월일: YYYYMMDD 형식으로 기재'
                      ) {
                        return (
                          <TableRow
                            sx={{
                              height: '48px',
                              lineHeight: '48px',
                              textAlign: 'center',
                              borderBottom:
                                i !== content.desc.length - 1
                                  ? '1px solid lightgray'
                                  : '',
                            }}
                          >
                            <Box
                              sx={{
                                width: '130px',
                                height: '48px',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <Form.MuiTextField
                                required={false}
                                type="date"
                                disabled={disabled}
                                {...register('no02_04')}
                              />
                            </Box>
                          </TableRow>
                        );
                      }
                      if (
                        content.desc[i] ===
                        "상병코드: 주진단명에 해당하는 '한국표준질병사인분류'의 분류기호를 1개만 기재(예: C16.10)"
                      ) {
                        return (
                          <TableRow
                            sx={{
                              height: '70px',
                              lineHeight: '70px',
                              textAlign: 'center',
                              borderBottom:
                                i !== content.desc.length - 1
                                  ? '1px solid lightgray'
                                  : '',
                            }}
                          >
                            <Box
                              sx={{
                                width: '130px',
                                height: '70px',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <Form.MuiTextField
                                required={false}
                                disabled={disabled}
                                {...register('no02_06')}
                              />
                            </Box>
                          </TableRow>
                        );
                      }
                      return (
                        <TableRow
                          sx={{
                            height:
                              content.desc[i] ===
                              '가입자 구분 (코드번호로 기재)'
                                ? '148px'
                                : content.desc[i] ===
                                  '입퇴원여부 (코드번호로 기재)'
                                ? '166px'
                                : content.id === 4 ||
                                  content.desc[i] === '성별(코드번호로 기재)'
                                ? '70px'
                                : '48px',
                            lineHeight:
                              content.desc[i] ===
                              '가입자 구분 (코드번호로 기재)'
                                ? '148px'
                                : content.desc[i] ===
                                  '입퇴원여부 (코드번호로 기재)'
                                ? '166px'
                                : content.id === 4 ||
                                  content.desc[i] === '성별(코드번호로 기재)'
                                ? '70px'
                                : '48px',
                            textAlign: 'center',
                            borderBottom:
                              i !== content.desc.length - 1
                                ? '1px solid lightgray'
                                : '',
                          }}
                        >
                          <Box
                            sx={{
                              width: '130px',
                              height:
                                content.desc[i] ===
                                '가입자 구분 (코드번호로 기재)'
                                  ? '148px'
                                  : content.desc[i] ===
                                    '입퇴원여부 (코드번호로 기재)'
                                  ? '166px'
                                  : content.id === 4 ||
                                    content.desc[i] === '성별(코드번호로 기재)'
                                  ? '70px'
                                  : '48px',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Form.MuiTextField
                              disabled={disabled}
                              required={false}
                              type="number"
                              inputProps={{ min, max }}
                              value={
                                content.desc[i].includes('가입자 구분')
                                  ? input1
                                  : content.desc[i].includes(
                                      String('입퇴원여부')
                                    )
                                  ? input2
                                  : content.desc[i].includes(String('성별'))
                                  ? input4
                                  : content.desc[i].includes(
                                      String('당일수술여부')
                                    )
                                  ? input5
                                  : 0
                              }
                              {...register(`no02_0${content.ids[i]}`, {
                                onChange: e => {
                                  var value = parseInt(e.target.value, 10);

                                  let max = { score };

                                  if (value >= max.score) value = max.score;
                                  if (value < min) value = min;

                                  if (content.desc[i].includes('가입자 구분'))
                                    setInput1(value);
                                  else if (
                                    content.desc[i].includes('입퇴원여부')
                                  )
                                    setInput2(value);

                                  if (content.desc[i].includes('성별'))
                                    setInput4(value);
                                  if (content.desc[i].includes('당일수술여부'))
                                    setInput5(value);
                                },
                              })}
                            />
                          </Box>
                        </TableRow>
                      );
                    })}
                  </StyledTableCellWithoutLeftRight>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
        <Box
          display={'flex'}
          flexDirection={'column'}
          alignItems={'flex-end'}
          sx={{ marginTop: '20px' }}
        >
          <Typography
            gutterBottom
            minWidth={115}
            fontWeight={700}
            variant="subtitle1"
          >
            {/* 합계: {total}점 */}
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default Contents1;
